/**
 * Centralized Route Registry for navigation and AI-powered search.
 * Single source of truth for all navigable routes, nav items, and search context.
 */

import { IconProps } from "@/components/library/Icon";
import { Permissions, RoleData } from "@/enums/user";
import { IntegrationTab, integrationTabRoute } from "@/types/integrations";
import type { NavAccessFields } from "@/utils/authz/navAccess";
import { CREATE_OR_UPDATE_LEVELS } from "@/utils/authz/routeGates";

/**
 * Navigation sections in the sidebar
 */
export enum NavSection {
  Dashboard = "dashboard",
  GailGpt = "gailgpt",
  Communications = "communications",
  General = "general",
  Resources = "resources",
}

/**
 * Tag keys that have translations in routes.json under "searchTags"
 * These are used for multi-language search
 */
type SearchTagKey =
  | "home"
  | "overview"
  | "main"
  | "start"
  | "analytics"
  | "metrics"
  | "reports"
  | "charts"
  | "data"
  | "insights"
  | "chat"
  | "ai"
  | "assistant"
  | "conversation"
  | "ask"
  | "help"
  | "gpt"
  | "history"
  | "past"
  | "previous"
  | "messages"
  | "inbound"
  | "agent"
  | "configuration"
  | "script"
  | "scenarios"
  | "setup"
  | "channels"
  | "routing"
  | "ivr"
  | "receiving"
  | "receptionist"
  | "handbook"
  | "outreach"
  | "outbound"
  | "marketing"
  | "sales"
  | "sequences"
  | "contacts"
  | "people"
  | "database"
  | "crm"
  | "customers"
  | "leads"
  | "phoneNumbers"
  | "campaigns"
  | "calls"
  | "bulk"
  | "dialer"
  | "activity"
  | "feed"
  | "recent"
  | "timeline"
  | "events"
  | "conversations"
  | "phone"
  | "voice"
  | "recordings"
  | "transcripts"
  | "callHistory"
  | "sms"
  | "whatsapp"
  | "messaging"
  | "inbox"
  | "assistants"
  | "bots"
  | "agents"
  | "virtual"
  | "automation"
  | "knowledge"
  | "files"
  | "documents"
  | "upload"
  | "training"
  | "docs"
  | "faq"
  | "workflows"
  | "processes"
  | "builder"
  | "flow"
  | "logic"
  | "integrations"
  | "apps"
  | "connect"
  | "thirdParty"
  | "webhooks"
  | "api"
  | "settings"
  | "preferences"
  | "account"
  | "profile"
  | "users"
  | "security"
  | "support"
  | "documentation"
  | "contact"
  | "learn"
  | "university"
  | "videos"
  | "marketplace"
  | "actions"
  | "redialing"
  | "businessHours"
  | "hours"
  | "holidays"
  | "schedule"
  | "openHours"
  | "coverages"
  | "extraction"
  | "outcomes"
  | "directory"
  | "lists"
  | "notifications"
  | "voices"
  | "calendar";

export type RouteSearchConfig = {
  icon: IconProps["icon"];
  /** Translation key from routes.json (usually under searchLabels) */
  labelKey: string;
  /** Narrower than the page gate — used when tabs share a dynamic route. */
  access?: NavAccessFields;
  requiresGailAgent?: boolean;
  /** Org has the Gail Agent product (`gailAgentEnabled`), independent of the caller's seat. */
  requiresOrgGailAgent?: boolean;
  requiresOutcomesFlag?: boolean;
};

/**
 * Route entry with all nav and search information
 */
export type RouteEntry = {
  /** Translated breadcrumb for routes without navigation or search metadata. */
  breadcrumbLabelKey?: string;
  // Core route info
  path: string;
  name: string;
  description: string;
  /** Tag keys for search - these get translated at runtime */
  tagKeys: SearchTagKey[];
  /** Palette metadata for routes that are not in the sidebar. */
  search?: RouteSearchConfig;

  // Nav item configuration (optional - routes without these don't appear in nav)
  nav?: {
    icon: IconProps["icon"];
    /** Translation key from routes.json */
    labelKey: string;
    /** Section this item belongs to */
    section: NavSection;
    /** Position in sidebar */
    position: "top" | "bottom";
    /** Permission data type for access control */
    dataType: RoleData;
    /**
     * Extra data types that also make this item visible. For a route whose tabs span several
     * resources (e.g. Knowledge), the entry shows when *any* of them is readable.
     */
    additionalDataTypes?: RoleData[];
    /** CRUD levels that grant visibility; omitted means Read. Any one level is enough. */
    levels?: readonly Permissions[];
    /** URL segments that mark this item as active */
    activeSegments?: string[];
    /** Additional active segment position (default: 0) */
    activePosition?: number;
    /** Open link in new tab */
    target?: "_blank";
    /** Data attributes for testing/tours */
    dataAttributes?: Record<string, string>;
    /** DOM id for tour targeting (used by NextStep) */
    tourId?: string;
    /** Sort order within section (lower = higher) */
    order?: number;
  };
};

/**
 * All navigable routes with nav configuration
 */
export const ROUTE_REGISTRY: RouteEntry[] = [
  // ============================================
  // Dashboard & Analytics Section
  // ============================================
  {
    path: "/",
    name: "Dashboard",
    description:
      "Main dashboard with overview of activity, analytics, and quick access to key features",
    tagKeys: ["home", "overview", "main", "start", "analytics", "metrics"],
    nav: {
      icon: "gauge-simple-high",
      labelKey: "dashboard",
      section: NavSection.Dashboard,
      position: "top",
      dataType: RoleData.Dashboard,
      activeSegments: [], // Active when no segments
      order: 1,
    },
  },
  {
    path: "/reporting",
    name: "Reporting",
    description: "Advanced reporting and analytics dashboard with charts and metrics",
    tagKeys: ["reports", "analytics", "charts", "metrics", "data", "insights"],
    nav: {
      icon: "chart-pie-simple",
      labelKey: "reporting",
      section: NavSection.Dashboard,
      position: "top",
      dataType: RoleData.Reporting,
      activeSegments: ["reporting"],
      order: 2,
    },
  },

  // ============================================
  // GailGPT & Chat Section
  // ============================================
  {
    path: "/gailgpt",
    name: "GailGPT Chat",
    description: "Start a new conversation with GailGPT AI assistant",
    tagKeys: ["chat", "ai", "assistant", "conversation", "ask", "help", "gpt"],
    nav: {
      icon: "comment",
      labelKey: "newChat",
      section: NavSection.GailGpt,
      position: "top",
      dataType: RoleData.GPT,
      activeSegments: ["gailgpt"],
      dataAttributes: { "data-tour": "nav-gailgpt" },
      tourId: "tour-nav-gailgpt",
      order: 1,
    },
  },
  {
    path: "/history",
    name: "Chat History",
    description: "View past conversations and chat history with GailGPT",
    tagKeys: ["history", "past", "conversations", "previous", "chat", "messages"],
    nav: {
      icon: "clock",
      labelKey: "history",
      section: NavSection.GailGpt,
      position: "top",
      dataType: RoleData.GPT,
      activeSegments: ["history"],
      order: 2,
    },
  },

  // ============================================
  // Communications Section
  // ============================================

  // Inbound
  {
    path: "/inbound/scripts",
    name: "Inbound Scripts",
    description: "Configure inbound scripts and call routing",
    tagKeys: ["inbound", "script", "routing", "ivr", "receiving"],
    nav: {
      icon: "arrow-right-to-arc",
      labelKey: "inbound",
      section: NavSection.Communications,
      position: "top",
      dataType: RoleData.Inbound,
      activeSegments: ["inbound"],
      dataAttributes: { "data-tour": "nav-inbound" },
      tourId: "tour-nav-inbound",
      order: 1,
    },
  },

  // Outbound
  {
    path: "/outbound/campaigns",
    name: "Outbound Campaigns",
    description: "Manage outbound campaigns, agents, and call automation",
    tagKeys: ["outreach", "outbound", "marketing", "sales", "campaigns"],
    nav: {
      icon: "arrow-left-to-arc",
      labelKey: "outbound",
      section: NavSection.Communications,
      position: "top",
      // Gated on Campaigns, not Outbound: the entry lands on `/outbound/campaigns`, so gating it on
      // `call` showed the link to roles the campaigns page then turned away, and hid it from roles
      // the page would have admitted (GAIL-785). `Outbound` still gates the directory and
      // data-extraction sub-tabs, which are reached from within the section.
      dataType: RoleData.Campaigns,
      activeSegments: ["outbound"],
      dataAttributes: { "data-tour": "nav-outreach" },
      tourId: "tour-nav-outreach",
      order: 2,
    },
  },

  // Contacts
  {
    path: "/communications/contacts",
    name: "Contacts",
    description: "Manage your contact database, add and edit contact records",
    tagKeys: ["contacts", "people", "database", "crm", "customers", "leads", "phoneNumbers"],
    nav: {
      icon: "address-card",
      labelKey: "contacts",
      section: NavSection.Communications,
      position: "top",
      dataType: RoleData.Contacts,
      activeSegments: ["contacts", "contact-lists"],
      activePosition: 1,
      dataAttributes: { "data-tour": "nav-contacts" },
      tourId: "tour-nav-contacts",
      order: 3,
    },
  },

  // Calls
  {
    path: "/calls",
    name: "Calls",
    description: "View and manage phone calls, call history, recordings and transcripts",
    tagKeys: ["phone", "voice", "recordings", "transcripts", "callHistory", "calls"],
    nav: {
      icon: "phone",
      labelKey: "calls",
      section: NavSection.Communications,
      position: "top",
      dataType: RoleData.Activity,
      activeSegments: ["calls"],
      order: 5,
    },
  },

  // Conversations
  {
    path: "/conversations",
    name: "Conversations",
    description: "View and manage messaging conversations across SMS, chat, and WhatsApp",
    tagKeys: ["messages", "sms", "chat", "whatsapp", "messaging", "inbox"],
    nav: {
      icon: "message-lines",
      labelKey: "conversations",
      section: NavSection.Communications,
      position: "top",
      dataType: RoleData.Messaging,
      activeSegments: ["conversations"],
      order: 6,
    },
  },

  // ============================================
  // General Section
  // ============================================

  // Assistants (Gails)
  {
    path: "/assistants",
    name: "Assistants",
    description: "Manage AI assistants, their configurations and capabilities",
    tagKeys: ["ai", "assistants", "bots", "agents", "virtual", "automation"],
    nav: {
      icon: "user-headset",
      labelKey: "gails",
      section: NavSection.General,
      position: "top",
      dataType: RoleData.Assistants,
      levels: CREATE_OR_UPDATE_LEVELS,
      activeSegments: ["assistants"],
      dataAttributes: { "data-tour": "gails-nav" },
      order: 1,
    },
  },

  // Knowledge
  {
    path: "/knowledge/files",
    name: "Knowledge Files",
    description: "Upload and manage knowledge base documents and files for AI training",
    tagKeys: ["knowledge", "files", "documents", "upload", "training", "ai", "docs", "faq"],
    nav: {
      icon: "book",
      labelKey: "knowledge",
      section: NavSection.General,
      position: "top",
      // Tabs span three resources: `files` is Knowledge (assistant), the agent-config tabs are
      // Agent (agent), and data extraction is Settings (organization). Any one of them at
      // create/update is reason to show the entry — read is the picker, not this page.
      dataType: RoleData.Knowledge,
      additionalDataTypes: [RoleData.Agent, RoleData.Settings],
      levels: CREATE_OR_UPDATE_LEVELS,
      activeSegments: ["knowledge"],
      order: 2,
    },
  },

  // Skills — lives in General with the other management surfaces (Gails,
  // Knowledge, Workflows), not in the GailGPT section: the GailGPT section
  // collapses to the single assistant entry while the dock is available,
  // and Skills must stay reachable from anywhere.
  {
    path: "/skills",
    name: "Skills",
    description: "Manage agent skills and capabilities",
    tagKeys: ["agents", "automation", "ai", "configuration"],
    nav: {
      icon: "puzzle-piece",
      labelKey: "skills",
      section: NavSection.General,
      position: "top",
      dataType: RoleData.Assistants,
      levels: CREATE_OR_UPDATE_LEVELS,
      activeSegments: ["skills"],
      order: 3,
    },
  },

  // Workflows
  {
    path: "/workflows",
    name: "Workflows",
    description: "Create and manage automation workflows",
    tagKeys: ["workflows", "automation", "processes", "builder", "flow", "logic"],
    nav: {
      icon: "circle-nodes",
      labelKey: "workflows",
      section: NavSection.General,
      position: "top",
      dataType: RoleData.Workflows,
      activeSegments: ["workflows"],
      order: 4,
    },
  },

  // Marketplace (integrations page)
  {
    path: "/marketplace",
    name: "Marketplace",
    description: "Browse and connect integrations from the marketplace",
    tagKeys: ["integrations", "apps", "connect", "thirdParty", "webhooks", "api"],
    nav: {
      icon: "wrench",
      labelKey: "integrations",
      section: NavSection.General,
      position: "top",
      dataType: RoleData.Integrations,
      activeSegments: ["marketplace", "integrations"],
      order: 5,
    },
  },

  // ============================================
  // Resources Section (Bottom)
  // ============================================

  // Gail University
  {
    path: "/gail-university",
    name: "Gail University",
    description: "Watch product training videos organized by topic and Gail product",
    tagKeys: ["learn", "university", "videos", "training", "help", "docs"],
    nav: {
      icon: "book",
      labelKey: "gailUniversity",
      section: NavSection.Resources,
      position: "bottom",
      dataType: RoleData.Support,
      activeSegments: ["gail-university"],
      order: 1,
    },
  },

  // Settings
  {
    path: "/settings/general",
    name: "General Settings",
    description: "Configure general application settings and preferences",
    tagKeys: [
      "settings",
      "configuration",
      "preferences",
      "account",
      "profile",
      "users",
      "security",
    ],
    nav: {
      icon: "gear",
      labelKey: "settings",
      section: NavSection.Resources,
      position: "bottom",
      dataType: RoleData.Settings,
      activeSegments: ["settings"],
      order: 2,
    },
  },

  // Support (External link)
  {
    path: "https://support.gail.com", // Will be replaced with BASE_SUPPORT_URL
    name: "Support",
    description: "Get help and access documentation",
    tagKeys: ["support", "help", "documentation", "contact", "faq"],
    nav: {
      icon: "circle-question",
      labelKey: "support",
      section: NavSection.Resources,
      position: "bottom",
      dataType: RoleData.Support,
      target: "_blank",
      order: 3,
    },
  },

  // ============================================
  // Non-nav routes (for search only)
  // ============================================
  {
    path: "/communications/contact-lists",
    name: "Contact Lists",
    description: "Create and manage contact lists for campaigns and outreach",
    tagKeys: ["contacts", "leads", "customers", "lists"],
    search: {icon: "list", labelKey: "searchLabels.contactLists"},
  },
  {
    path: "/outbound/scripts",
    name: "Outbound Scripts",
    description: "View and manage outbound call scripts",
    tagKeys: ["script", "outbound", "calls"],
    search: {icon: "file-lines", labelKey: "searchLabels.outboundScripts"},
  },
  {
    path: "/outbound/directory",
    name: "Outbound Directory",
    description: "Manage outbound directory with contacts and transfer rules",
    tagKeys: ["contacts", "people", "outbound", "directory"],
    search: {icon: "address-card", labelKey: "searchLabels.outboundDirectory"},
  },
  {
    path: "/outbound/actions",
    name: "Outbound Actions",
    description: "Browse and manage action templates for outbound campaigns",
    tagKeys: ["scenarios", "outbound", "actions"],
    search: {icon: "bolt", labelKey: "searchLabels.outboundActions"},
  },
  {
    path: "/outbound/sequences",
    name: "Outbound Sequences",
    description: "Manage call sequences for outbound campaigns",
    tagKeys: ["outbound", "sequences", "campaigns"],
    search: {icon: "list-ol", labelKey: "searchLabels.outboundSequences"},
  },
  {
    path: "/outbound/redialing-rules",
    name: "Outbound Redialing Rules",
    description: "Configure redialing and retry rules for outbound calls",
    tagKeys: ["outbound", "redialing", "calls"],
    search: {icon: "arrows-rotate", labelKey: "searchLabels.outboundRedialingRules"},
  },
  {
    path: "/outbound/data-extraction",
    name: "Outbound Data Extraction",
    description: "Configure data extraction fields for outbound call processing",
    tagKeys: ["data", "configuration", "outbound", "extraction"],
    search: {icon: "file-lines", labelKey: "searchLabels.outboundDataExtraction"},
  },
  {
    path: "/inbound/actions",
    name: "Inbound Actions",
    description: "Browse and manage action templates for inbound calls",
    tagKeys: ["scenarios", "inbound", "actions"],
    search: {icon: "bolt", labelKey: "searchLabels.inboundActions"},
  },
  {
    path: "/inbound/directory",
    name: "Directory",
    description: "Manage agency directory with team members and extensions",
    tagKeys: ["contacts", "people", "agents", "directory", "inbound"],
    search: {icon: "address-card", labelKey: "searchLabels.inboundDirectory"},
  },
  {
    path: "/inbound/transfer-rules",
    name: "Transfer Rules",
    description: "Configure call transfer rules and routing logic",
    tagKeys: ["routing", "calls", "configuration", "inbound"],
    search: {icon: "arrow-right-to-arc", labelKey: "searchLabels.inboundTransferRules"},
  },
  {
    path: "/inbound/data-extraction",
    name: "Inbound Data Extraction",
    description: "Configure data extraction fields for inbound call processing",
    tagKeys: ["data", "configuration", "inbound", "extraction"],
    search: {icon: "file-lines", labelKey: "searchLabels.inboundDataExtraction"},
  },
  {
    path: "/inbound/calendar",
    name: "Inbound Calendar",
    description: "View the weekly inbound schedule and open a script or holiday from a time block",
    tagKeys: ["inbound", "calendar", "schedule", "hours", "holidays"],
    search: {icon: "calendar", labelKey: "searchLabels.inboundCalendar"},
  },
  {
    path: "/knowledge/hours",
    name: "Business Hours",
    description: "Configure business hours and availability schedules",
    tagKeys: [
      "knowledge",
      "configuration",
      "setup",
      "businessHours",
      "hours",
      "holidays",
      "schedule",
      "openHours",
    ],
    search: {
      icon: "clock",
      labelKey: "searchLabels.businessHours",
      access: {dataType: RoleData.Agent, levels: CREATE_OR_UPDATE_LEVELS},
      requiresGailAgent: true,
    },
  },
  {
    path: "/knowledge/faqs",
    name: "FAQs",
    description: "Manage frequently asked questions for AI responses",
    tagKeys: ["faq", "help", "support", "knowledge"],
    search: {
      icon: "circle-question",
      labelKey: "searchLabels.faqs",
      access: {dataType: RoleData.Agent, levels: CREATE_OR_UPDATE_LEVELS},
      requiresGailAgent: true,
    },
  },
  {
    path: "/knowledge/coverages",
    name: "Coverages",
    description: "Define coverage areas and service regions",
    tagKeys: ["knowledge", "configuration", "setup", "coverages"],
    search: {
      icon: "book",
      labelKey: "searchLabels.coverages",
      access: {dataType: RoleData.Agent, levels: CREATE_OR_UPDATE_LEVELS},
      requiresGailAgent: true,
    },
  },
  {
    path: "/knowledge/dataExtraction",
    name: "Data Extraction",
    description: "Configure data extraction fields for call processing",
    tagKeys: ["data", "configuration", "knowledge", "extraction"],
    search: {
      icon: "file-lines",
      labelKey: "searchLabels.dataExtraction",
      access: {dataType: RoleData.Settings, levels: CREATE_OR_UPDATE_LEVELS},
      requiresGailAgent: true,
    },
  },
  {
    path: "/knowledge/outcomes",
    name: "Outcomes",
    description: "Manage organization call outcome catalog for scripts and redialing",
    tagKeys: ["data", "configuration", "knowledge", "outcomes"],
    search: {
      icon: "flag",
      labelKey: "searchLabels.outcomes",
      access: {dataType: RoleData.Agent, levels: CREATE_OR_UPDATE_LEVELS},
      requiresGailAgent: true,
      requiresOutcomesFlag: true,
    },
  },
  {
    path: integrationTabRoute(IntegrationTab.Partners),
    name: "Partner Integrations",
    description: "Connect and manage third-party partner integrations",
    tagKeys: ["integrations", "connect", "thirdParty", "apps"],
    search: {icon: "wrench", labelKey: "searchLabels.partnerIntegrations"},
  },
  {
    path: integrationTabRoute(IntegrationTab.Calendar),
    name: "Calendar Integration",
    description: "Connect calendar services for scheduling appointments",
    tagKeys: ["integrations", "connect", "calendar"],
    search: {icon: "calendar", labelKey: "searchLabels.calendarIntegration"},
  },
  {
    path: integrationTabRoute(IntegrationTab.CustomTools),
    name: "Custom Tools",
    description: "Create and manage custom integration tools",
    tagKeys: ["api", "webhooks", "automation", "integrations"],
    search: {icon: "wrench", labelKey: "searchLabels.customTools"},
  },
  {
    path: integrationTabRoute(IntegrationTab.Webhooks),
    name: "Webhooks",
    description: "Configure webhook endpoints for external integrations",
    tagKeys: ["webhooks", "api", "automation"],
    search: {icon: "plug", labelKey: "searchLabels.webhooks"},
  },
  {
    path: "/settings/details",
    name: "Organization Details",
    description: "Manage organization profile and business details",
    tagKeys: ["settings", "profile", "configuration"],
    search: {icon: "building", labelKey: "searchLabels.organizationDetails"},
  },
  {
    path: "/settings/voices",
    name: "Custom Voices",
    description: "Manage custom AI voice settings and configurations",
    tagKeys: ["voice", "ai", "settings", "voices"],
    search: {
      icon: "microphone",
      labelKey: "searchLabels.customVoices",
      requiresOrgGailAgent: true,
    },
  },
  {
    path: "/settings/notifications",
    name: "Notifications",
    description: "Configure notification preferences and alerts",
    tagKeys: ["settings", "preferences", "notifications"],
    search: {
      icon: "bell",
      labelKey: "searchLabels.notifications",
      requiresOrgGailAgent: true,
    },
  },
  {
    path: "/settings/user-access",
    name: "User Access",
    description: "Manage user accounts, roles, and permissions",
    tagKeys: ["users", "security", "settings"],
    search: {icon: "users", labelKey: "searchLabels.userAccess"},
  },
  {
    path: "/settings/security",
    name: "Security",
    description: "Configure security settings and access controls",
    tagKeys: ["security", "settings"],
    search: {icon: "lock", labelKey: "searchLabels.security"},
  },
  {
    path: "/profile",
    name: "Profile",
    description: "Manage your user profile, theme, and language settings",
    tagKeys: ["profile", "account", "preferences", "settings"],
    search: {icon: "user", labelKey: "searchLabels.profile"},
  },
  {
    path: "/profile/calendar",
    name: "Calendar Settings",
    description: "Configure your personal calendar and availability",
    tagKeys: ["profile", "settings", "calendar"],
    search: {icon: "calendar", labelKey: "searchLabels.calendarSettings"},
  },
  {
    path: "/partners",
    name: "Partners",
    description: "View and manage partner connections",
    tagKeys: ["integrations", "connect"],
    search: {icon: "wrench", labelKey: "searchLabels.partners"},
  },
  {
    path: "/permissions",
    name: "Permissions",
    description: "View your access permissions across the application",
    tagKeys: ["security", "users", "settings"],
    search: {icon: "key", labelKey: "searchLabels.permissions"},
  },
  {
    path: "/smart-onboarding",
    breadcrumbLabelKey: "smartOnboarding",
    name: "Smart Onboarding",
    description: "Guided setup wizard for configuring your account",
    tagKeys: ["setup", "configuration", "start"],
  },
  {
    path: "/get-gail",
    breadcrumbLabelKey: "start",
    name: "Get Started",
    description: "Get started with Gail and set up your account",
    tagKeys: ["start", "setup"],
  },
  {
    path: "/upgrade",
    breadcrumbLabelKey: "upgrade",
    name: "Upgrade",
    description: "View pricing plans and upgrade your subscription",
    tagKeys: ["account", "settings"],
  },
];

// ============================================
// Helper functions
// ============================================

/**
 * Get all nav items (routes with nav configuration)
 */
export function getNavRoutes(): RouteEntry[] {
  return ROUTE_REGISTRY.filter((route) => route.nav !== undefined);
}

/**
 * Get routes by section
 */
export function getRoutesBySection(section: NavSection): RouteEntry[] {
  return ROUTE_REGISTRY.filter((route) => route.nav?.section === section);
}

/**
 * Translation function type for tag translation
 */
type TagTranslator = (key: string) => string;

/**
 * Translate tag keys to localized strings
 * @param tagKeys - Array of tag keys from a route
 * @param t - Translation function (from useTranslation)
 * @returns Array of translated tags
 */
export function translateTags(tagKeys: SearchTagKey[], t: TagTranslator): string[] {
  return tagKeys.map((key) => t(`searchTags.${key}`));
}

/**
 * Route with translated tags for search
 */
type TranslatedRouteEntry = Omit<RouteEntry, "tagKeys"> & {
  tags: string[];
};

/**
 * Get all routes with translated tags
 * @param t - Translation function (from useTranslation("routes"))
 */
export function getTranslatedRoutes(t: TagTranslator): TranslatedRouteEntry[] {
  return ROUTE_REGISTRY.map((route) => ({
    ...route,
    tags: translateTags(route.tagKeys, t),
  }));
}

/**
 * Get all routes formatted as context for AI prompt
 * @param t - Optional translation function for localized tags
 */
export function getRouteContextForPrompt(t?: TagTranslator): string {
  return ROUTE_REGISTRY.map((r) => {
    const tags = t ? translateTags(r.tagKeys, t) : r.tagKeys;
    return `- ${r.path}: ${r.name} - ${r.description} (tags: ${tags.join(", ")})`;
  }).join("\n");
}

/**
 * Find a route by path
 */
export function findRouteByPath(path: string): RouteEntry | undefined {
  return ROUTE_REGISTRY.find((r) => r.path === path);
}

/**
 * Resolve the best matching registry entry for a live pathname, using longest-prefix
 * matching so dynamic detail routes like `/outbound/campaigns/123` resolve correctly.
 */
export function resolveRouteFromPathname(
  pathname: string,
): RouteEntry | undefined {
  if (!pathname) return undefined;

  // Plain string ops, not regex, to avoid ReDoS on a user-controlled pathname.
  let path = pathname;
  const queryIndex = path.indexOf("?");
  if (queryIndex !== -1) path = path.slice(0, queryIndex);
  const hashIndex = path.indexOf("#");
  if (hashIndex !== -1) path = path.slice(0, hashIndex);

  let end = path.length;
  while (end > 1 && path.charCodeAt(end - 1) === 47 /* "/" */) end--;
  const cleaned = path.slice(0, end) || "/";

  const exact = ROUTE_REGISTRY.find((r) => r.path === cleaned);
  if (exact) return exact;

  let best: RouteEntry | undefined;
  for (const route of ROUTE_REGISTRY) {
    if (route.path === "/") continue;
    if (cleaned === route.path || cleaned.startsWith(route.path + "/")) {
      if (!best || route.path.length > best.path.length) {
        best = route;
      }
    }
  }
  return best;
}

/**
 * Get searchable routes with their tag keys
 */
export function getSearchableRoutes(): Pick<
  RouteEntry,
  "path" | "name" | "description" | "tagKeys"
>[] {
  return ROUTE_REGISTRY.map(({ path, name, description, tagKeys }) => ({
    path,
    name,
    description,
    tagKeys,
  }));
}

/**
 * Get searchable routes with translated tags
 * @param t - Translation function (from useTranslation("routes"))
 */
export function getSearchableRoutesTranslated(
  t: TagTranslator
): Pick<TranslatedRouteEntry, "path" | "name" | "description" | "tags">[] {
  return ROUTE_REGISTRY.map(({ path, name, description, tagKeys }) => ({
    path,
    name,
    description,
    tags: translateTags(tagKeys, t),
  }));
}
